import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderSearch, Link2 } from 'lucide-react';
import { api } from '@/lib/api';
import { POSTED_LABEL, postedKey, type PostedType } from '@/lib/posted';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { PostedList } from '@/components/PostedList';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { BackLink } from './config/_shared';

/**
 * Gepostete Nachrichten (Werkzeuge): everything the bot posted from the
 * dashboard on this server — open, edit (→ matching editor), update, delete —
 * plus taking over older bot messages via their Discord message link.
 */
export function PostedMessagesPage() {
  const { guildId } = useParams<{ guildId: string }>();
  const qc = useQueryClient();
  const [link, setLink] = useState('');
  const [type, setType] = useState<PostedType | ''>('');

  const adopt = useMutation({
    mutationFn: () =>
      api<{ posted: { type: PostedType } }>(`/api/guilds/${guildId}/posted/adopt`, {
        method: 'POST',
        body: JSON.stringify({ link: link.trim(), ...(type ? { type } : {}) }),
      }),
    onSuccess: () => {
      setLink('');
      setType('');
      qc.invalidateQueries({ queryKey: postedKey(guildId!) });
    },
  });

  if (!guildId) return null;
  return (
    <div className="mx-auto max-w-4xl">
      <BackLink guildId={guildId} />
      <h1 className="mb-1 text-2xl font-semibold">Gepostete Nachrichten</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Alles, was der Bot aus dem Dashboard gepostet hat. Bearbeiten öffnet den passenden Editor,
        Aktualisieren überschreibt die Nachricht in Discord mit dem aktuellen Stand.
      </p>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Link2 className="h-4 w-4 text-primary" /> Ältere Nachricht übernehmen
          </CardTitle>
          <CardDescription>
            In Discord bei der Bot-Nachricht „Nachrichtenlink kopieren“ und hier einfügen. Die Art wird
            automatisch erkannt.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (link.trim()) adopt.mutate();
            }}
          >
            <div className="min-w-0 flex-1 basis-64">
              <Label>Nachrichtenlink</Label>
              <Input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://discord.com/channels/…/…/…"
              />
            </div>
            <div className="basis-48 max-md:flex-1">
              <Label>Art</Label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={type}
                onChange={(e) => setType(e.target.value as PostedType | '')}
              >
                <option value="">Automatisch erkennen</option>
                {Object.entries(POSTED_LABEL)
                  .filter(([k]) => k !== 'nsfw')
                  .map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
              </select>
            </div>
            <Button type="submit" disabled={!link.trim() || adopt.isPending}>
              {adopt.isPending ? 'Prüfe…' : 'Übernehmen'}
            </Button>
          </form>
          {adopt.isSuccess && (
            <p className="mt-2 text-sm text-success">
              Übernommen als {POSTED_LABEL[adopt.data.posted.type]} ✓
            </p>
          )}
          {adopt.error && <p className="mt-2 text-sm text-destructive">{(adopt.error as Error).message}</p>}
        </CardContent>
      </Card>

      <AdoptChannelCard guildId={guildId} />

      <Card>
        <CardContent className="pt-6">
          <PostedList guildId={guildId} showType showEdit />
        </CardContent>
      </Card>
    </div>
  );
}

interface AdoptChannelResult {
  scanned: number;
  alreadyKnown: number;
  adopted: Array<{ type: PostedType; messageId: string }>;
  skippedMessages: number;
  noRight: number;
}

/** All bot messages of one channel at once — picked from the list or by ID. */
function AdoptChannelCard({ guildId }: { guildId: string }) {
  const qc = useQueryClient();
  const [channelId, setChannelId] = useState('');
  const [includeMessages, setIncludeMessages] = useState(false);
  const validId = /^\d{17,20}$/.test(channelId.trim());

  const scan = useMutation({
    mutationFn: () =>
      api<AdoptChannelResult>(`/api/guilds/${guildId}/posted/adopt-channel`, {
        method: 'POST',
        body: JSON.stringify({ channelId: channelId.trim(), includeMessages }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: postedKey(guildId) }),
  });

  const r = scan.data;
  const counts = r
    ? Object.entries(
        r.adopted.reduce<Record<string, number>>((acc, a) => {
          acc[a.type] = (acc[a.type] ?? 0) + 1;
          return acc;
        }, {})
      )
        .map(([t, n]) => `${n}× ${POSTED_LABEL[t as PostedType]}`)
        .join(', ')
    : '';

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FolderSearch className="h-4 w-4 text-primary" /> Ganzen Channel übernehmen
        </CardTitle>
        <CardDescription>
          Der Bot durchsucht die letzten 100 Nachrichten des Channels und übernimmt alle seine eigenen
          Panels. Schon übernommene werden übersprungen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <Label>Channel auswählen</Label>
            <ChannelPicker
              guildId={guildId}
              value={validId ? channelId.trim() : null}
              onChange={(v) => setChannelId(v ?? '')}
              types={[CHANNEL_TYPE.GUILD_TEXT, CHANNEL_TYPE.GUILD_ANNOUNCEMENT]}
            />
          </div>
          <div>
            <Label>… oder Channel-ID</Label>
            <Input value={channelId} onChange={(e) => setChannelId(e.target.value)} placeholder="z. B. 1364686428247818240" />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
          <div className="min-w-0">
            <div className={includeMessages ? 'text-sm' : 'text-sm text-muted-foreground'}>
              Auch normale Embeds und Nachrichten übernehmen
            </div>
            <div className="text-[11px] text-muted-foreground">
              z. B. Ankündigungen — nicht Welcome-/Level-Up-Nachrichten
            </div>
          </div>
          <Switch
            checked={includeMessages}
            onChange={(e) => setIncludeMessages(e.target.checked)}
            aria-label="Auch normale Embeds und Nachrichten übernehmen"
          />
        </div>
        <div className="flex items-center gap-3">
          <Button disabled={!validId || scan.isPending} onClick={() => scan.mutate()}>
            {scan.isPending ? 'Durchsuche…' : 'Channel durchsuchen'}
          </Button>
          {channelId.trim() && !validId && <span className="text-xs text-destructive">Keine gültige Channel-ID.</span>}
        </div>
        {r && (
          <p className={r.adopted.length ? 'text-sm text-success' : 'text-sm text-muted-foreground'}>
            {r.adopted.length ? `${r.adopted.length} übernommen: ${counts}.` : 'Nichts Neues gefunden.'}{' '}
            <span className="text-muted-foreground">
              {r.scanned} Bot-Nachrichten gefunden
              {r.alreadyKnown ? `, ${r.alreadyKnown} schon in der Liste` : ''}
              {r.skippedMessages ? `, ${r.skippedMessages} normale Nachrichten übersprungen` : ''}
              {r.noRight ? `, ${r.noRight} ohne Recht übersprungen` : ''}.
            </span>
          </p>
        )}
        {scan.error && <p className="text-sm text-destructive">{(scan.error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}
